import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type { PageQuery, PageResult } from '../types/result';

/** 外仓供货指令（`GY` 单）：外仓代履，出库即生成院内"入即出"配对双单。 */
export interface SupplyRow {
  id: string;
  bizNo: string;
  orgId: string;
  demandDeptId: string;
  demandDeptName: string | null;
  sourceApplyId: string;
  sourceBizNos: string | null;
  outerWarehouseId: string;
  outerWarehouseName?: string | null;
  triggerType: string;
  supplyStatus: string;
  totalQty: number;
  totalAmount: number;
  isEmergency: boolean;
  slaDeadline: string | null;
  isOvertime: boolean;
  notifyAt: string | null;
  outAt: string | null;
  createAt: string;
}

export interface SupplyItemRow {
  id: string;
  lineNo: number;
  applyItemId: string;
  materialId: string;
  materialCode: string | null;
  materialName: string | null;
  spec: string | null;
  unitId: string;
  unitName: string | null;
  planQty: number;
  outQty: number;
  qtyBase: number;
  batchNo: string | null;
  expiryDate: string | null;
  price: number;
  amount: number;
  lineStatus: string | null;
}

export interface SupplyDetail {
  order: SupplyRow;
  items: SupplyItemRow[];
}

/**
 * 供应商视角的指令单头（`/api/wl/supply` 在 SUPPLIER 门户下的字段白名单）：
 * 后端刻意不返回院内机构/来源申领单/合计成本，供应商只能看到履约必需的信息。
 */
export interface SupplierSupplyOrder {
  id: string;
  bizNo: string;
  demandDeptName: string | null;
  outerWarehouseName: string | null;
  triggerType: string;
  supplyStatus: string;
  totalQty: number;
  isEmergency: boolean;
  slaDeadline: string | null;
  isOvertime: boolean;
  notifyAt: string | null;
  outAt: string | null;
  createAt: string;
}

export interface SupplierSupplyDetail {
  order: SupplierSupplyOrder;
  items: SupplyItemRow[];
}

export interface SupplyListQuery extends PageQuery {
  supplyStatus?: string;
  sourceApplyId?: string;
}

/** 出库确认结果：返回配对的院内入库单/出库单号。 */
export interface SupplyOutResult {
  supplyOrderId: string;
  supplyBizNo: string;
  supplyStatus: string;
  inboundBizNo: string;
  outboundBizNo: string;
  warehouseId: string;
  warehouseName: string | null;
  totalQty: number;
  isOvertime: boolean;
}

/** 外仓供货指令：列表 / 详情 / 出库确认（事务内生成 RK + CK 配对单与双流水）。 */
export function createSupplyApi(http: HttpClient) {
  return {
    /** 列表。供应商门户返回字段白名单版本（`SupplierSupplyOrder`），故用泛型声明期望形态。 */
    list<T = SupplyRow>(query: SupplyListQuery = {}): Promise<PageResult<T>> {
      return http.request<PageResult<T>>({ path: '/wl/supply', query: toQuery(query) });
    },
    /**
     * 详情。同一端点两种视角：院内返回 `SupplyDetail`，供应商门户返回 `SupplierSupplyDetail`
     * 的字段白名单版本，故用泛型由调用方声明期望形态。
     */
    detail<T = SupplyDetail>(id: Id): Promise<T> {
      return http.request<T>({ path: `/wl/supply/${id}` });
    },
    /** 出库确认：幂等（后端 IdempotencyFilter）。 */
    out(id: Id): Promise<SupplyOutResult> {
      return http.request<SupplyOutResult>({ path: `/wl/supply/${id}/out`, method: 'POST' });
    }
  };
}

export type SupplyApi = ReturnType<typeof createSupplyApi>;
